/**
 * Welcome / Landing Page
 *
 * Marketing landing page for alpha access requests.
 * Features game branding, character cards, and alpha request form.
 */

import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { submitBetaRequest } from '../services/betaService';
import styles from './WelcomePage.module.css';

// Import images from assets
import heroImage from '../../assets/Screenshot 2025-12-18 214635.png';
import knightImage from '../../assets/Screenshot 2025-12-18 214643.png';
import wizardImage from '../../assets/Screenshot 2025-12-18 214649.png';
import ninjaImage from '../../assets/Screenshot 2025-12-18 214654.png';

interface FloatingPixel {
  id: number;
  left: string;
  delay: string;
  duration: string;
}

/**
 * Source options for "How did you hear about us?"
 * Values must match backend VALID_SOURCES: twitter, reddit, discord, youtube, friend, search, other
 */
const SOURCE_OPTIONS = [
  { value: '', label: 'How did you hear about us?' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'discord', label: 'Discord' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'friend', label: 'Friend or Family' },
  { value: 'search', label: 'Search Engine' },
  { value: 'other', label: 'Other' },
];

export function WelcomePage() {
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('');
  const [motivation, setMotivation] = useState('');
  const [discordHandle, setDiscordHandle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pixels, setPixels] = useState<FloatingPixel[]>([]);
  const [hasAccount, setHasAccount] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  // Generate floating pixels on mount
  useEffect(() => {
    const newPixels: FloatingPixel[] = [];
    for (let i = 0; i < 30; i++) {
      newPixels.push({
        id: i,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 5}s`,
        duration: `${3 + Math.random() * 4}s`,
      });
    }
    setPixels(newPixels);
  }, []);

  // Check if user has visited before (returning user)
  useEffect(() => {
    const hasAccountStored = localStorage.getItem('has_account');
    const hasVisitedBefore = localStorage.getItem('visited_welcome');
    
    // Mark that user has visited the welcome page
    if (!hasVisitedBefore) {
      localStorage.setItem('visited_welcome', 'true');
    }
    
    // If returning user with account, update state
    if (hasAccountStored === 'true' && !user) {
      setHasAccount(true);
    }
  }, [user]);

  // If user is already logged in, redirect to home
  if (user) {
    navigate('/home', { replace: true });
    return null;
  }

  /**
   * Validate email format
   */
  function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!source) {
      setError('Please tell us how you heard about us');
      return;
    }

    if (!motivation.trim()) {
      setError('Please tell us what excites you about the game');
      return;
    }

    if (motivation.trim().length < 20) {
      setError('Please write at least a few sentences');
      return;
    }

    setLoading(true);

    try {
      const result = await submitBetaRequest({
        email: email.trim(),
        source,
        motivation: motivation.trim(),
        discordHandle: discordHandle.trim() || undefined,
      });

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message);
      }
    } catch {
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.container}>
      {/* Floating Pixels */}
      <div className={styles.floatingPixels}>
        {pixels.map((pixel) => (
          <div
            key={pixel.id}
            className={styles.pixel}
            style={{
              left: pixel.left,
              animationDelay: pixel.delay,
              animationDuration: pixel.duration,
            }}
          />
        ))}
      </div>

      {/* Header with Login */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>Drawn of War</div>
          <nav className={styles.nav}>
            <Link to="/about" className={styles.aboutLink}>
              About
            </Link>
            {hasAccount && (
              <span className={styles.welcomeBack}>Welcome back!</span>
            )}
            <Link to="/login" className={styles.loginButton}>
              {hasAccount ? 'Sign In to Continue' : 'Sign In'}
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Card Layout */}
        <div className={styles.cardGrid}>
          {/* Hero Banner - Full Width */}
          <div className={`${styles.card} ${styles.heroCard}`}>
            <img src={heroImage} alt="Drawn of War" className={styles.heroImage} />
          </div>

          {/* Bottom Row: Character Cards + CTA */}
          <div className={styles.bottomRow}>
            {/* Knight Card */}
            <div className={styles.card}>
              <img src={knightImage} alt="Knight" className={styles.cardImage} />
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>Knight</h3>
                <p className={styles.cardDescription}>
                  Honorable warriors who lead the charge into battle with sword and shield.
                </p>
              </div>
            </div>

            {/* Wizard Card */}
            <div className={styles.card}>
              <img src={wizardImage} alt="Wizard" className={styles.cardImage} />
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>Wizard</h3>
                <p className={styles.cardDescription}>
                  Masters of arcane arts who bend reality to their will.
                </p>
              </div>
            </div>

            {/* Ninja Card */}
            <div className={styles.card}>
              <img src={ninjaImage} alt="Ninja" className={styles.cardImage} />
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>Ninja</h3>
                <p className={styles.cardDescription}>
                  Silent assassins who strike from the shadows with deadly precision.
                </p>
              </div>
            </div>

            {/* CTA Card */}
            <div className={`${styles.card} ${styles.ctaCard}`}>
              <div className={styles.ctaContent}>
                <h2 className={styles.ctaTitle}>Join the Alpha</h2>
                <p className={styles.ctaDescription}>
                  Be among the first to shape the future of AI-powered tactical combat.
                </p>

                {success ? (
                  <div className={styles.successState}>
                    <div className={styles.successIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <p className={styles.successText}>
                      Thanks for signing up! We'll be in touch soon.
                    </p>
                    <Link to="/login" className={styles.loginLink}>
                      Already approved? Sign in
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className={styles.form}>
                    {error && (
                      <div className={styles.errorMessage} role="alert">
                        {error}
                      </div>
                    )}
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="Email address *"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      className={styles.input}
                    />
                    <select
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      disabled={loading}
                      className={styles.select}
                    >
                      {SOURCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <textarea
                      placeholder="What excites you about Drawn of War? *"
                      value={motivation}
                      onChange={(e) => setMotivation(e.target.value)}
                      disabled={loading}
                      rows={3}
                      className={styles.textarea}
                    />
                    <input
                      type="text"
                      placeholder="Discord username (optional)"
                      value={discordHandle}
                      onChange={(e) => setDiscordHandle(e.target.value)}
                      disabled={loading}
                      className={styles.input}
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className={styles.submitButton}
                    >
                      {loading ? (
                        <span className={styles.spinner} />
                      ) : (
                        'Request Access'
                      )}
                    </button>
                    <div className={styles.formFooter}>
                      <span className={styles.loginPrompt}>Already have access?</span>
                      <Link to="/login" className={styles.loginLink}>
                        Sign in
                      </Link>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <Link to="/about" className={styles.footerLink}>About</Link>
          <span className={styles.footerDivider}>•</span>
          <a
            href="https://discord.gg/YFTVtMVG4s"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Discord
          </a>
          <span className={styles.footerDivider}>•</span>
          <Link to="/login" className={styles.footerLink}>Sign In</Link>
        </div>
        <p className={styles.copyright}>&copy; 2026 Drawn of War</p>
      </footer>
    </div>
  );
}
